import type {
  CatalogueDraftRow,
  CatalogueRow,
  DraftBatchItem,
} from "@/api/types";

/**
 * Normalised view-model rendered by `<EntryDetail>`. Three callsites
 * feed it from three different source shapes:
 *
 * - **Catalogue rows** — opaque `Record<string, string|number|boolean|null>`
 *   (the `tools_and_services` Sheet via `GET /api/catalogue`). Values
 *   arrive as Sheet-native strings (`"3.40"`, `"TRUE"`, `"tag1, tag2"`)
 *   so each field passes through a coercion helper below.
 * - **Draft rows** — same opaque shape from `tools_draft`, with two
 *   staging-only fields (`discovery_source`, `curator_notes`).
 * - **DraftBatchItem** — the typed Zod contract surfaced by the HITL
 *   review endpoint, where most fields are already typed.
 *
 * Keeping all three normalisations here lets `<EntryDetail>` stay purely
 * presentational. The raw row is still passed alongside so the
 * "Show raw JSON" toggle can pretty-print the unmodified source.
 */
export interface EntryDetailView {
  name: string;
  vendor: string;
  url: string;
  category: string;
  subcategory: string | null;
  problemTags: ReadonlyArray<string>;
  pricingModel: string;
  /** `-1` ⇒ "contact sales"; `null` ⇒ unknown. */
  pricingFrom: number | null;
  confidenceScore: number | null;
  sourceCount: number | null;
  qualityScore: number | null;
  qualityTagsScore: number | null;
  qualityEmbeddingScore: number | null;
  qualityConfidenceScore: number | null;
  qualityNotes: string | null;
  qualitySources: string | null;
  validatorFlag: boolean | null;
  embeddingText: string | null;
  integrations: string | null;
  companySizeFit: string | null;
  verified: string | null;
  lastUpdated: string | null;
  sourceUrl: string | null;
  sourceUrls: string | null;
  status: string | null;
  notes: string | null;
  /** Drafts-only staging field — null when the view came from a promoted catalogue row. */
  discoverySource: string | null;
  /** Drafts-only staging field — null when the view came from a promoted catalogue row. */
  curatorNotes: string | null;
}

type CellValue = string | number | boolean | null | undefined;

function coerceString(value: CellValue): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

function coerceNumber(value: CellValue): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function coerceBool(value: CellValue): boolean | null {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.trim().toUpperCase();
    if (v === "TRUE") return true;
    if (v === "FALSE") return false;
  }
  return null;
}

function coerceTags(value: CellValue): ReadonlyArray<string> {
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Sheet-shaped rows (catalogue + drafts) both expose the same column
 * lookup via the `[key: string]: ...` index signature; the rest of the
 * row's typed fields differ (`daysUntilStale` vs `jobId`/`jobNumber`).
 * This shared helper consumes only the index-signature surface so both
 * adapters can call into it without TypeScript flagging the extra
 * required props.
 */
type SheetLikeRow = Record<string, string | number | boolean | null>;

function buildSheetView(row: SheetLikeRow): EntryDetailView {
  return {
    name: coerceString(row.name) ?? "",
    vendor: coerceString(row.vendor) ?? "",
    url: coerceString(row.url) ?? "",
    category: coerceString(row.category) ?? "",
    subcategory: coerceString(row.subcategory),
    problemTags: coerceTags(row.problem_tags),
    pricingModel: coerceString(row.pricing_model) ?? "",
    pricingFrom: coerceNumber(row.pricing_from),
    confidenceScore: coerceNumber(row.confidence_score),
    sourceCount: coerceNumber(row.source_count),
    qualityScore: coerceNumber(row.quality_score),
    qualityTagsScore: coerceNumber(row.quality_tags_score),
    qualityEmbeddingScore: coerceNumber(row.quality_embedding_score),
    qualityConfidenceScore: coerceNumber(row.quality_confidence_score),
    qualityNotes: coerceString(row.quality_notes),
    qualitySources: coerceString(row.quality_sources),
    validatorFlag: coerceBool(row.validator_flag),
    embeddingText: coerceString(row.embedding_text),
    integrations: coerceString(row.integrations),
    companySizeFit: coerceString(row.company_size_fit),
    // `verified` is intentionally surfaced as the raw Sheet string — the
    // column carries a TRUE/FALSE *display* value the operator may edit
    // by hand to record off-platform verification state, not a strict
    // boolean. Coercing to bool would lose the unknown-third-value
    // posture the catalogue UI relies on.
    verified: coerceString(row.verified),
    lastUpdated: coerceString(row.last_updated),
    sourceUrl: coerceString(row.source_url),
    sourceUrls: coerceString(row.source_urls),
    status: coerceString(row.status),
    notes: coerceString(row.notes),
    discoverySource: null,
    curatorNotes: null,
  };
}

/**
 * Build the view from one promoted-catalogue row. Mirrors the columns
 * in `TOOLS_AND_SERVICES_HEADERS` (see packages/google/src/schemas.ts);
 * staging-only fields (`discovery_source`, `curator_notes`) are set to
 * null because they don't exist on the promoted side.
 */
export function viewFromCatalogueRow(row: CatalogueRow): EntryDetailView {
  return buildSheetView(row);
}

/**
 * Build the view from one in-flight draft row. Same fields as the
 * catalogue mapping plus the two staging-only fields the curator emits
 * and the validator strips on promotion.
 */
export function viewFromDraftRow(row: CatalogueDraftRow): EntryDetailView {
  return {
    ...buildSheetView(row),
    discoverySource: coerceString(row.discovery_source),
    curatorNotes: coerceString(row.curator_notes),
  };
}

/**
 * Build the view from a HITL (human-in-the-loop) review batch item.
 * Most fields are already typed on the Zod schema; the only fields not
 * surfaced by the contract are the auxiliary metadata columns
 * (integrations, company-size fit, verified, last_updated, source URLs,
 * status, notes) which the review flow doesn't carry — those land as
 * `null` and the corresponding rows in the detail grid simply read `—`.
 *
 * `embeddingText` flows through after the schema extension (issue #218).
 */
export function viewFromBatchItem(item: DraftBatchItem): EntryDetailView {
  return {
    name: item.name,
    vendor: item.vendor,
    url: item.url,
    category: item.category,
    subcategory: item.subcategory,
    problemTags: item.problemTags,
    pricingModel: item.pricingModel,
    pricingFrom: item.pricingFrom,
    confidenceScore: item.confidenceScore,
    sourceCount: item.sourceCount,
    qualityScore: item.qualityScore,
    qualityTagsScore: null,
    qualityEmbeddingScore: null,
    qualityConfidenceScore: null,
    qualityNotes: item.qualityNotes,
    qualitySources: null,
    validatorFlag: item.validatorFlag,
    embeddingText: item.embeddingText,
    integrations: null,
    companySizeFit: null,
    verified: null,
    lastUpdated: null,
    sourceUrl: null,
    sourceUrls: null,
    status: null,
    notes: null,
    discoverySource: null,
    curatorNotes: null,
  };
}
